/**
 * A saved dashboard, drawn.
 *
 * ONE component for reading and for editing. The difference between the two is
 * whether the grid's drag handles are live and whether the bar reports its
 * coverage — not a second render path, because a second path is how an editor
 * ends up arranging something that looks different once saved.
 */
import { applyFilterAction, type FilterAction, filterTypeOf } from '@clockwise/report-kit';
import type {
  DashboardDefinition,
  DashboardTile,
  FilterNode,
  ReportModel,
  ReportSpec,
} from '@clockwise/semantic-core';
import {
  describeFilters,
  isDashboardReport,
  isLinkedReport,
  MAX_DASHBOARD_FILTERS,
  parseDefinition,
} from '@clockwise/semantic-core';
import type { DateRangeValue } from '@clockwise/shared/date-range';
import { resolveDateRange } from '@clockwise/shared/date-range';
import { FormulaHintProvider } from '@clockwise/ui';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Layout } from 'react-grid-layout';
import type { DrillStep } from '@/components/reports/drill-chain';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { analyticsApi, reportDefinitionsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { describeView, fieldLabel } from '@/lib/saved-view';
import { DashboardGrid } from '../dashboard-grid';
import { DRILL_KEY, layoutWithDrill, stripDrill } from '../drill-layout';
import { AddTile } from './add-tile';
import { DashboardFilterBar } from './dashboard-filter-bar';
import {
  barFields,
  dashboardPeriods,
  deriveDashboardFilters,
  effectiveSpec,
  type FilterCandidate,
  filterStateFor,
  isSet,
  pinnedFields,
  resolveTiles,
  saysSomething,
  withPinned,
  withTileFilters,
} from './filters';
import { metricComparisonSpecs } from './metric-window';
import { NoteSettingsSheet } from './note-settings-sheet';
import {
  addMetricTile,
  applyNoteSettings,
  removeTile,
  tileFull,
  toLayout,
  withLayout,
} from './tile-editing';
import { TileEditor } from './tile-editor';
import { tilePalette } from './tile-palette';
import { applyTileSettings } from './tile-settings';
import { TileSettingsSheet } from './tile-settings-sheet';
import { DataTile, TextTile, type TileNotice, TombstoneTile } from './tiles';
import { useFilterLabels } from './use-filter-labels';
import { type TileDrillContext, useTileDrill } from './use-tile-drill';

/**
 * How long the bar waits on a tile before serving what it has.
 *
 * Long enough that a slow network is still just "loading" — the flash this
 * guards against lasts a few hundred milliseconds — and short enough that a
 * request which will never answer does not cost the reader their filters.
 */
const STUCK_AFTER_MS = 10_000;

interface Props {
  definition: DashboardDefinition;
  /** The bar's live state. Owned by the route, because it lives in the URL. */
  period: DateRangeValue;
  onPeriodChange: (next: DateRangeValue) => void;
  /** The reader's filter rules, pinned chips' values included. */
  filters: readonly FilterNode[];
  /**
   * Change them. An UPDATER rather than a next list: the bar can report two
   * actions in one event (a value, then its panel closing), and a list built
   * from this render's props would lose the first.
   */
  onFiltersChange: (update: (prev: readonly FilterNode[]) => readonly FilterNode[]) => void;
  /**
   * Editing: the whole draft definition flows back through here.
   *
   * ONE callback rather than separate layout / tiles / view handlers, because
   * every edit is a change to the same object and the route has to hold that
   * object anyway to save it. `undefined` means read-only.
   */
  onDefinitionChange?: (next: DashboardDefinition) => void;
  /**
   * May the viewer edit saved reports (`report.edit`)? Decides whether a
   * report tile's menu links to its report for editing, and whether a
   * dashboard report may be saved as a custom report.
   */
  mayEditReports?: boolean;
  /**
   * "Save as custom report" on a dashboard report. The route owns it: it acts
   * on the SAVED dashboard and has to reconcile the draft with the answer.
   */
  onPromoteTile?: (tileId: string) => void;
}

export function DashboardView({
  definition,
  period,
  onPeriodChange,
  filters,
  onFiltersChange,
  onDefinitionChange,
  mayEditReports = false,
  onPromoteTile,
}: Props) {
  const editing = onDefinitionChange !== undefined;

  // Read LENIENTLY. A tile written by a newer deploy, or one whose contents no
  // longer parse, becomes a tombstone rather than taking the dashboard down
  // with it — losing eleven working tiles to punish a twelfth is the failure
  // this guards against.
  const parsed = useMemo(() => parseDefinition(definition), [definition]);

  const models = useQuery({
    queryKey: queryKeys.analytics.models(),
    queryFn: ({ signal }) => analyticsApi.models(signal).then((r) => r.data),
    // The catalog changes on deploy, not during a session.
    staleTime: Number.POSITIVE_INFINITY,
  });

  // The reports the tiles point at. DISTINCT ids, so the same report placed
  // twice is fetched once.
  // Linked tiles only: a dashboard report carries its own and needs no fetch.
  const reportIds = useMemo(
    () => [...new Set(parsed.tiles.flatMap((t) => (isLinkedReport(t) ? [t.reportId] : [])))],
    [parsed.tiles],
  );
  const reports = useQueries({
    queries: reportIds.map((id) => ({
      queryKey: queryKeys.reportDefinitions.detail(id),
      queryFn: () => reportDefinitionsApi.get(id).then((r) => r.data),
      staleTime: 60_000,
      // A report that was deleted or unshared answers 404, and that is not an
      // error to retry — it is a tombstone. Retrying would delay the tile's
      // only honest message by several seconds.
      retry: false,
    })),
  });

  /** Report id → what came back, so a tile can find its own without an index. */
  const byReportId = useMemo(() => {
    const map = new Map<string, { spec?: ReportSpec; name?: string; failed: boolean }>();
    reportIds.forEach((id, i) => {
      const q = reports[i];
      map.set(id, { spec: q?.data?.spec, name: q?.data?.name, failed: q?.isError === true });
    });
    return map;
  }, [reportIds, reports]);

  /**
   * The question a tile asks, before the dashboard's bar and period.
   *
   * A linked tile's is its report's WITH the tile's own filters ANDed on —
   * they are the tile's own answer, so the bar's merge rule must see them as
   * such (`tileOwned`), exactly as it sees a dashboard report's own filters.
   */
  const specOf = useMemo(
    () =>
      (tile: DashboardTile): ReportSpec | undefined => {
        if (tile.type === 'metric' || isDashboardReport(tile)) return tile.spec;
        if (isLinkedReport(tile)) {
          const saved = byReportId.get(tile.reportId)?.spec;
          return saved ? withTileFilters(saved, tile.filters ?? []) : undefined;
        }
        return undefined;
      },
    [byReportId],
  );

  const modelOf = useMemo(() => {
    const map = new Map<string, ReportModel>((models.data ?? []).map((m) => [m.id, m]));
    return (id: string) => map.get(id);
  }, [models.data]);

  const resolved = useMemo(
    () => resolveTiles(parsed.tiles, specOf, modelOf),
    [parsed.tiles, specOf, modelOf],
  );

  const candidates = useMemo(() => deriveDashboardFilters(resolved), [resolved]);

  /**
   * THE BAR'S FIELDS ARE DERIVED FROM THE TILES, so they must not be served
   * from a partial set of them.
   *
   * `deriveDashboardFilters` unions the tiles' vocabularies, and a union taken
   * over a SUBSET is missing fields. So a bar drawn while some tiles are still
   * in flight would have pinned chips pop in, and a "+ Filter" menu gaining
   * entries under the pointer. A metric tile carries its spec inline and
   * resolves the moment the model catalog does, while a report tile waits on
   * a fetch of its own; a dashboard mixing the two is exactly that case.
   */
  const settled = !models.isPending && reports.every((r) => !r.isPending);

  // ONE period drives every tile, so the bar offers only what every tile's
  // model offers — a dashboard holding a Budget vs Actuals tile has no custom
  // range, because that tile's own builder would never have offered one.
  // Custom waits for every tile to be known; see `dashboardPeriods`.
  const periodPresets = useMemo(
    () => dashboardPeriods(parsed.tiles, specOf, modelOf, settled),
    [parsed.tiles, specOf, modelOf, settled],
  );

  const derivedFields = useMemo(
    () => barFields(candidates, parsed.filters, { editing, tileCount: resolved.length }),
    [candidates, parsed.filters, editing, resolved.length],
  );

  /**
   * The last fields derived from a SETTLED set of tiles.
   *
   * Held so that a tile arriving later updates the bar rather than blanking
   * it. Waiting from scratch every time is right on first paint and wrong
   * afterwards: adding a report tile while editing starts one more request,
   * and clearing the chips — with the coverage notes an author is reading to
   * decide what to pin — would take the bar away at the moment it is being
   * worked on.
   */
  const lastSettledFields = useRef<FilterCandidate[] | null>(null);
  useEffect(() => {
    if (settled) lastSettledFields.current = derivedFields;
  }, [settled, derivedFields]);

  /**
   * A HANG IS NOT AN ERROR, and this is what bounds the wait for one.
   *
   * Nothing converts a request that never answers into a failure: there is no
   * client-side timeout, and these queries do not retry. So `settled` alone
   * would wait forever, and a dashboard whose first paint met a hung report
   * would show placeholders where its filters belong for as long as it stayed
   * open, saying nothing about why.
   *
   * Past this point the bar SAYS SO rather than guessing. Serving the partial
   * fields would be the other option, and it is the wrong one: the reader
   * would be handed a bar that quietly disagrees with the tiles under it. An
   * unavailable filter bar is a fact somebody can act on; a subtly wrong one
   * is not.
   */
  const [stuck, setStuck] = useState(false);
  /**
   * The tile in a settings panel — `null` while adding one — which panel (a
   * note has its own), and whether it is open. Two pieces of state so closing
   * does not clear the tile: the panel keeps it (and its key) while it
   * animates out.
   */
  const [configuring, setConfiguring] = useState<{
    tileId: string | null;
    kind: 'report' | 'note';
  } | null>(null);
  const [configureOpen, setConfigureOpen] = useState(false);
  /** Bumped per open, so reopening a tile starts a fresh builder draft. */
  const [configureSession, setConfigureSession] = useState(0);
  useEffect(() => {
    if (settled) {
      setStuck(false);
      return;
    }
    const timer = setTimeout(() => setStuck(true), STUCK_AFTER_MS);
    return () => clearTimeout(timer);
  }, [settled]);

  const fields = settled ? derivedFields : (lastSettledFields.current ?? []);

  /**
   * The bar could not be built, and there is nothing standing in for it.
   *
   * `models.isError` belongs here too: the catalog failing leaves every
   * vocabulary unknown, which produced an empty bar indistinguishable from a
   * dashboard whose tiles genuinely share no dimensions.
   *
   * Fields held over from an earlier settle are NOT an error — they are a real
   * answer, just one tile out of date, and the tile itself shows its own state.
   */
  const barUnavailable = (stuck || models.isError) && lastSettledFields.current === null;

  // Placeholders only while the wait is still honest. Once settled fields
  // exist they stay on screen; once the wait is given up the notice takes over.
  const barPending = !settled && !stuck && lastSettledFields.current === null;

  const pinned = useMemo(() => pinnedFields(fields, parsed.filters), [fields, parsed.filters]);

  // SAVING a filter change: the bar's action, applied with the same function
  // the report builder's reducer uses, against the route's latest rules.
  const typeOf = useMemo(() => filterTypeOf(fields), [fields]);
  const onFilterAction = useCallback(
    (action: FilterAction) => onFiltersChange((prev) => applyFilterAction(prev, action, typeOf)),
    [onFiltersChange, typeOf],
  );

  // Pinning is an EDIT — it changes the bar for everybody, so it goes into the
  // draft and waits for "Save for everyone" like moving a tile does.
  // Saved pins the bar cannot show yet are KEPT on a toggle, and still count
  // against the cap — see `withPinned`.
  const known = useMemo(() => new Set(fields.map((f) => f.id)), [fields]);
  const onPinnedChange = useMemo(
    () =>
      onDefinitionChange
        ? (next: string[]) =>
            onDefinitionChange({ ...parsed, filters: withPinned(parsed.filters, next, known) })
        : undefined,
    [onDefinitionChange, parsed, known],
  );
  const pinLimit = MAX_DASHBOARD_FILTERS - (parsed.filters.length - pinned.length);

  /** Fields the bar's rules are actually narrowing — the tile notices. */
  const setFields = useMemo(
    () => fields.filter((field) => isSet(filters, field.id)),
    [fields, filters],
  );

  /**
   * What the TILES run: the bar's rules, once they stop changing.
   *
   * A text or number chip commits on every keystroke, and every commit is a
   * new spec for every tile — twelve tiles and an eight-letter word was
   * ninety-six queries for half-typed values. The bar itself stays live.
   */
  const applied = useDebouncedValue(filters, 300);

  // The window every tile runs over. `resolveDateRange` re-answers a macro, so
  // "current month" is about whatever month this is being opened in.
  const window = useMemo(
    () => resolveDateRange(period) ?? { from: parsed.period.from, to: parsed.period.to },
    [period, parsed.period],
  );

  /**
   * The ONE drill open on this dashboard: which tile, and its chain. Reading,
   * not editing — so it is state here, never the definition, and entering edit
   * mode closes it (a drill row in the grid would be dragged about with the
   * tiles).
   *
   * It remembers the period and bar it was opened under, and lapses when either
   * changes: its chain pins a coordinate of THAT question, and replayed over the
   * tile's new one it lands on rows — or an empty month — that no longer add
   * up to anything on screen.
   */
  const [opened, setDrill] = useState<{
    tileId: string;
    chain: DrillStep[];
    applied: readonly FilterNode[];
    window: typeof window;
  } | null>(null);
  const drill = opened && opened.applied === applied && opened.window === window ? opened : null;
  const [drillHost, setDrillHost] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (editing) setDrill(null);
  }, [editing]);
  const drillTile =
    !editing && drill && parsed.tiles.some((t) => t.id === drill.tileId) ? drill.tileId : null;

  // The drill row goes directly under the clicked tile's row, so the reader
  // keeps the tile in view while reading what is behind it.
  const gridLayout = useMemo<Layout>(
    () => layoutWithDrill(toLayout(parsed), drillTile),
    [parsed, drillTile],
  );

  const openConfigure = (tileId: string | null, kind: 'report' | 'note' = 'report') => {
    setConfiguring({ tileId, kind });
    setConfigureSession((n) => n + 1);
    setConfigureOpen(true);
  };
  const configuredTile = configuring?.tileId
    ? (parsed.tiles.find((t) => t.id === configuring.tileId) ?? null)
    : null;
  const configuredNote = configuredTile?.type === 'text' ? configuredTile : null;

  // One provider for every tile's ƒ hints — metric headers and table column
  // headers alike — so moving across the board skips the hover delay.
  return (
    <FormulaHintProvider>
      <div className="flex min-h-0 flex-1 flex-col gap-4">
        <DashboardFilterBar
          period={period}
          onPeriodChange={onPeriodChange}
          window={window}
          periodPresets={periodPresets}
          fields={fields}
          filters={filters}
          onAction={onFilterAction}
          onClear={() => onFiltersChange(() => [])}
          pinned={pinned}
          onPinnedChange={onPinnedChange}
          // ANY rule that says something, not only those on a field a tile has
          // now: a filter in a shared link for a removed tile has no chip, and
          // Clear is the one control that can still take it off.
          anySet={filters.some(saysSomething)}
          pinLimit={pinLimit}
          pending={barPending}
          pendingChips={parsed.filters.length}
          unavailable={barUnavailable}
        />

        {editing && onDefinitionChange && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <AddTile
              full={tileFull(parsed.tiles)}
              // The Configure panel, opened with its Report section empty: adding a
              // report and configuring one are the same panel.
              onAddReport={() => openConfigure(null)}
              onAddMetric={(model, measure, label, trend) =>
                onDefinitionChange(addMetricTile(parsed, model, measure, label, trend))
              }
              onAddText={() => openConfigure(null, 'note')}
            />
            {/* What everyone opens on — the saved view, not the bar. Changing it
                is the question "Save for everyone" asks. */}
            <span data-testid="saved-view-status" className="text-xs text-muted-foreground">
              Opens on{' '}
              {describeView({ filters: parsed.defaultFilters, period: parsed.period }, (field) =>
                fieldLabel(models.data as ReportModel[] | undefined, field),
              )}
            </span>
          </div>
        )}

        {parsed.tiles.length === 0 ? (
          <p className="rounded-md border border-dashed bg-card p-8 text-center text-sm text-muted-foreground">
            {editing
              ? 'Add a tile to get started: build a report here, or use one of your custom reports.'
              : 'This dashboard has no tiles yet.'}
          </p>
        ) : (
          <DashboardGrid
            layout={gridLayout}
            // A read-only dashboard still hands RGL a handler: it fires on the
            // library's own responsive reflow, and swallowing those would fight
            // it. Nothing is persisted without an explicit Save either way.
            onLayoutChange={
              onDefinitionChange
                ? (next) => onDefinitionChange(withLayout(parsed, stripDrill(next)))
                : () => {}
            }
          >
            {parsed.tiles.map((tile, i) => (
              <div key={tile.id}>
                <Tile
                  tile={tile}
                  palette={tilePalette(i)}
                  report={isLinkedReport(tile) ? byReportId.get(tile.reportId) : undefined}
                  specOf={specOf}
                  modelOf={modelOf}
                  filters={applied}
                  window={window}
                  period={period}
                  setFields={setFields}
                  models={models.data as ReportModel[] | undefined}
                  drill={
                    editing
                      ? undefined
                      : {
                          chain: drill?.tileId === tile.id ? drill.chain : undefined,
                          onChainChange: (chain) =>
                            setDrill(
                              chain.length > 0 ? { tileId: tile.id, chain, applied, window } : null,
                            ),
                          host: drillTile === tile.id ? drillHost : null,
                          mayBuild: mayEditReports,
                        }
                  }
                  edit={
                    onDefinitionChange && {
                      mayEditReport: mayEditReports,
                      onConfigure: () =>
                        openConfigure(tile.id, tile.type === 'text' ? 'note' : 'report'),
                      onPromote:
                        onPromoteTile && isDashboardReport(tile)
                          ? () => onPromoteTile(tile.id)
                          : undefined,
                      onRemove: () => onDefinitionChange(removeTile(parsed, tile.id)),
                    }
                  }
                />
              </div>
            ))}
            {drillTile && (
              // The grid clones its children and puts its OWN ref on them, so the
              // tray's portal target is an element inside the grid item.
              <div key={DRILL_KEY}>
                <div
                  ref={setDrillHost}
                  className="flex h-full min-h-0 flex-col overflow-auto rounded-xl border bg-card"
                />
              </div>
            )}
          </DashboardGrid>
        )}

        {onDefinitionChange && configuring?.kind === 'report' && (
          <TileSettingsSheet
            // Remounted per OPEN: the panel owns its draft from the tile.
            // Unchanged by closing, so the panel animates out; new per open.
            key={configureSession}
            open={configureOpen}
            tile={configuredTile}
            // The palette the tile will be painted with once placed, so the
            // builder's preview shows the colours the tile will actually have. A
            // new tile is appended, so it takes the next position.
            palette={tilePalette(
              configuredTile ? parsed.tiles.indexOf(configuredTile) : parsed.tiles.length,
            )}
            models={models.data as ReportModel[] | undefined}
            period={period}
            window={window}
            onApply={(settings, fallbackTitle) => {
              onDefinitionChange(
                applyTileSettings(parsed, configuring.tileId, settings, fallbackTitle),
              );
              setConfigureOpen(false);
            }}
            onClose={() => setConfigureOpen(false)}
          />
        )}
        {onDefinitionChange &&
          configuring?.kind === 'note' &&
          // A note that is no longer there is not drawn as "Add a note": its
          // Apply would edit nothing and say nothing.
          (configuring.tileId === null || configuredNote) && (
            <NoteSettingsSheet
              key={configureSession}
              open={configureOpen}
              tile={configuredNote}
              onApply={(note) => {
                onDefinitionChange(applyNoteSettings(parsed, configuredNote?.id ?? null, note));
                setConfigureOpen(false);
              }}
              onClose={() => setConfigureOpen(false)}
            />
          )}
      </div>
    </FormulaHintProvider>
  );
}

/** The editing callbacks a tile gets in edit mode; absent when reading. */
interface TileEdit {
  mayEditReport: boolean;
  onConfigure: () => void;
  onPromote?: () => void;
  onRemove: () => void;
}

function Tile({
  tile,
  report,
  specOf,
  modelOf,
  filters,
  window,
  period,
  setFields,
  models,
  drill,
  edit,
  palette,
}: {
  tile: DashboardTile;
  /** The catalog, for the drill's landings. */
  models?: readonly ReportModel[];
  /** The dashboard's drill, as this tile sees it; absent while editing. */
  drill?: TileDrillContext;
  palette?: readonly string[];
  report?: { spec?: ReportSpec; name?: string; failed: boolean };
  specOf: (t: DashboardTile) => ReportSpec | undefined;
  modelOf: (id: string) => ReportModel | undefined;
  filters: readonly FilterNode[];
  window: { from: string; to: string };
  period: DateRangeValue;
  /** The bar's fields that are narrowing something now. */
  setFields: readonly FilterCandidate[];
  edit?: TileEdit;
}) {
  const controls = edit ? (
    <TileEditor
      tile={tile}
      onConfigure={edit.onConfigure}
      mayEditReport={edit.mayEditReport}
      onPromote={edit.onPromote}
      onRemove={edit.onRemove}
    />
  ) : undefined;

  // The tile's OWN filters, for the chips under its title — not the report's
  // (a linked report's own are part of what the report IS) and not the bar's.
  const ownNodes =
    tile.type === 'metric' || isDashboardReport(tile)
      ? tile.spec.filters
      : isLinkedReport(tile)
        ? (tile.filters ?? NO_FILTERS)
        : NO_FILTERS;
  const ownSpec = specOf(tile);
  const ownModel = ownSpec ? modelOf(ownSpec.model) : undefined;
  const labelOf = useFilterLabels(ownModel, ownNodes, window);
  const ownFilters = useMemo(
    () => (ownModel ? describeFilters(ownModel, ownNodes.filter(saysSomething), labelOf) : []),
    [ownModel, ownNodes, labelOf],
  );

  // What the tile RUNS: its report with the dashboard's period and bar applied.
  // Computed before the early returns below, because the drill needs it too —
  // the question behind the number a reader clicks is this one, not the stored
  // spec, or the rows a drill lands on would not add up to the figure.
  const view = tile.type === 'report' ? tile.view : undefined;
  const preset = specPeriod(period);
  const spec = useMemo(
    () =>
      ownSpec && ownModel
        ? effectiveSpec({
            spec: ownSpec,
            model: ownModel,
            active: filters,
            period: { preset, from: window.from, to: window.to },
            view,
          })
        : null,
    [ownSpec, ownModel, filters, preset, window.from, window.to, view],
  );
  // A trended metric reports the reader's WHOLE window against its mirror —
  // "This quarter" against the quarter before — as two questions of their own.
  const comparison = useMemo(
    () => (spec ? metricComparisonSpecs(spec, { from: window.from, to: window.to }) : null),
    [spec, window.from, window.to],
  );

  const title = tile.title ?? report?.name ?? 'Report';
  // Report and number tiles drill; notes and tombstones have nothing to drill
  // into. A number's figure drills into the window it reports: the comparison's
  // own question when it has one, so the landing is the reader's window rather
  // than the history the series reached back for — and ties out to the figure.
  const drills = tile.type === 'report' || tile.type === 'metric';
  const tileDrill = useTileDrill({
    models,
    source: {
      id: isLinkedReport(tile) ? tile.reportId : undefined,
      name: title,
      spec: drills ? (comparison?.current ?? spec) : null,
    },
    drill: drills ? drill : undefined,
  });

  if (tile.type === 'text') {
    return <TextTile title={tile.title} body={tile.body} controls={controls} editing={!!edit} />;
  }
  if (tile.type === 'unknown') {
    return <TombstoneTile title={tile.title} reason="unknown-type" controls={controls} />;
  }
  if (report?.failed) {
    return <TombstoneTile title={tile.title} reason="missing-report" controls={controls} />;
  }

  const saved = ownSpec;
  const model = ownModel;
  if (!saved || !model || !spec) {
    // Still loading, or a model this build does not know. `DataTile` renders
    // the loading state; a genuinely unknown model never resolves and stays
    // there, which is the honest outcome for a tile nothing can draw.
    return (
      <DataTile
        title={tile.title ?? report?.name ?? '…'}
        spec={null}
        model={null}
        controls={controls}
      />
    );
  }

  const notices = noticesFor(setFields, { tileId: tile.id, spec: saved, model }, filters);

  return (
    <>
      <DataTile
        title={title}
        spec={spec}
        model={model}
        notices={notices}
        ownFilters={ownFilters}
        controls={controls}
        onDrill={tileDrill.onDrill}
        // The DASHBOARD's window, not the tile's: a metric widened its own to
        // fetch history, and the reader's choice is what decides which period it
        // reports.
        selectedPeriod={window}
        comparison={comparison}
        palette={palette}
      />
      {tileDrill.portals}
    </>
  );
}

const NO_FILTERS: readonly FilterNode[] = [];

/**
 * What this tile should say about the bar's filters.
 *
 * Only for filters that are SET, and only two of the three states earn a line.
 * `applied` is the normal case and needs no chrome — a note under every tile
 * saying "filtered by Customer" trains people to stop reading the line that
 * matters. And telling a reader that a tile is not filtered by something
 * nobody filtered by is noise.
 */
function noticesFor(
  setFields: readonly FilterCandidate[],
  tile: { tileId: string; spec: ReportSpec; model: ReportModel },
  filters: readonly FilterNode[],
): TileNotice[] {
  const out: TileNotice[] = [];
  for (const field of setFields) {
    const state = filterStateFor(tile, field.id, filters);
    if (state === 'tileOwned' || state === 'notApplicable') {
      out.push({ label: field.label, state });
    }
  }
  return out;
}

/** A macro a spec can carry; `custom` and the two unsupported ones drop out. */
function specPeriod(period: DateRangeValue): ReportSpec['period'] {
  const preset = period.preset;
  if (preset === 'custom' || preset === 'all_time' || preset === 'current_week') return undefined;
  return preset;
}
